<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useSessionStore } from 'src/store';
import Box from 'src/components/BoxContainer.vue';

const { t } = useI18n();
const router = useRouter();
const sessionStore = useSessionStore();

const isLoading = ref(true);

const detectOS = () => {
  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  if (/android/.test(ua)) {
    if (/huawei|xiaomi|oppo|vivo|meizu/.test(ua) ||
      (/^zh(-|_)?/.test(navigator.language))) {
      return 'chinese-android';
    }
    return 'android';
  }

  if (/ipad|iphone|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }

  if (/win/.test(platform)) return 'windows';
  if (/mac/.test(platform)) return 'macos';
  if (/linux/.test(platform)) return 'linux';

  return 'unknown';
};

const getOS = computed(() => detectOS());

const videoUrl = computed(() => sessionStore.appDownloadInfo?.installationSupportVideoURL || null);
const showAppDownloadVideo = computed(() => sessionStore.appDownloadInfo?.showAppDownloadVideo);
const showDownloadButton = computed(() => {
  const os = getOS.value;
  const preamble = sessionStore.appDownloadInfo?.preambleResult;
  return (os === 'android' || os === 'chinese-android') && preamble?.appStoreLinks?.androidURL || os === 'ios' && preamble?.appStoreLinks?.iosurl;
});

const downloadApp = () => {
  const os = getOS.value;
  const preamble = sessionStore.appDownloadInfo?.preambleResult;

  switch (os) {
    case 'android':
    case 'chinese-android':
      window.open(preamble?.appStoreLinks?.androidURL, '_blank');
      break;
    case 'ios':
      window.open(preamble?.appStoreLinks?.iosurl, '_blank');
      break;
    default:
      router.push('/Activate');
      break;
  }
};

const goToPWE = () => {
  router.push('/Activate');
};

watch(() => sessionStore.appDownloadInfo, (newVal) => {
  if (newVal) {
    isLoading.value = false;
  }
}, { immediate: true });

onMounted(() => {
    sessionStore.setIsHeaderVisible(true);
    sessionStore.setIsFooterVisible(true);
  document.querySelectorAll('html')[0].scrollTop = 0;
});
</script>

<template>
  <q-page class="app-download" padding>
      <div class="row justify-center">
        <Box class="col-auto"
          :box-drop-shadow="$zb.enums.BoxDropShadowEnum.lg"
          :box-margin="$zb.enums.BoxMarginEnum.mb40"
          :box-padding="$zb.enums.BoxPaddingEnum.p504535"
          :box-width="$zb.enums.BoxWidthEnum.md">
            <div class="row mb-30" v-if="showAppDownloadVideo">
              <div class="col">
                <zbm-skeleton-loader :loading="isLoading" height="300px" width="100%"/>

                <div v-if="videoUrl && !isLoading" class="video-container">
                  <video controls autoplay muted playsinline>
                    <source :src="videoUrl" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>

            <div class="row justify-center mb-30">
              <zbm-skeleton-loader height="50px" :loading="isLoading" width="150px">
                <img src="/img/mymobility-logo.svg" alt="myMobility" style="width: 150px; height: auto;" />
              </zbm-skeleton-loader>
            </div>

            <div class="row justify-center mb-30">
              <zbm-skeleton-loader :dark="false" height="34px" :loading="isLoading" width="200px">
                <h3 class="text-center">{{ t('download_app_webpage_header') }}</h3>
              </zbm-skeleton-loader>
            </div>

            <div class="font-size-20 mb-30 text-center">
              <zbm-skeleton-loader height="60px" :loading="isLoading" width="100%">
                <div v-html="t('download_app_webpage_paragraph')"></div>
              </zbm-skeleton-loader>
            </div>

            <div v-if="showDownloadButton || isLoading" class="row justify-center mb-30 download-btn">
              <zbm-skeleton-loader class="q-btn--276" height="50px" width="200px" :loading="isLoading">
                <zbm-btn 
                  class="q-btn--276"
                  :color="$zb.enums.ButtonStdColorEnum.accentText"
                  :icon="getOS === 'ios' ? 'fa-brands fa-apple' : 'fa-brands fa-android'"
                  :label="t('download_app_webpage_download_button')"
                  :data-testid="getOS === 'ios' ? 'iosDownloadBtn' : 'androidDownloadBtn'"
                  @click="downloadApp"
                />
              </zbm-skeleton-loader>
            </div>

            <div class="row justify-center">
              <zbm-skeleton-loader height="20px" :loading="isLoading" width="200px">
                <zbm-btn-link 
                  :color="$zb.enums.ButtonStdColorEnum.accentText"
                  :label="t('download_app_webpage_no_smart_phone')"
                  data-testid="noSmartphoneLink"
                  @click="goToPWE"
                />
              </zbm-skeleton-loader>
            </div>
        </Box>
      </div>
  </q-page>
</template>

<style lang="scss" scoped>
.app-download {
  padding-inline: 0;

  .box {
    min-height: 609px;
  }

  .video-container {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    overflow: hidden;
    border-radius: $generic-border-radius-md;

    video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover; 
      display: block;
    }
  }

  @supports not (aspect-ratio: 16 / 9) {
    .video-container {
      padding-top: 56.25%; 
    }
  }

  @media (max-width: 768px) {
    .video-container {
      aspect-ratio: 4 / 3;
    }
  }

  @media (max-width: 480px) {
    .video-container {
      aspect-ratio: 1 / 1;
    }
  }

  .q-btn {
    font-size: 18px;
    padding-inline: 24px;

    :deep(.on-left) {
      margin-inline-end: 20px;
    }

    &--276 {
      min-width: 276px;
      max-width: 100%;
    }
  }

  .download-btn {
    .q-btn {
      padding-block: 12px;

      :deep(.q-icon) {
        font-size: 24px;
      }
    }
  }
}
</style>

